import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';

import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TabControlService } from 'src/app/services/tab-control.service';
import { ValidationService } from 'src/app/services/validators/validation.service';
import { environment } from 'src/environments/environment';
import * as Constants from "../../../constants/constants";
import { ApiService } from 'src/app/services/api/api.service';
import { organizeNames, organizeValues } from 'src/app/util/Helper';
import { createIDsValidatorEstate } from 'src/app/services/validators/custom.validators';
import { NotificationService } from 'src/app/services/notification.service';
import { ExpansionPanel } from 'src/app/components/section/section.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-step4-guardian',
  templateUrl: './step4-guardian.component.html',
  styleUrls: ['./step4-guardian.component.scss'],
  animations: [
    trigger('bounceIn', [
      state('in', style({ opacity: 1, transform: 'scale(1)' })),
      transition('void => *', [
        style({ opacity: 0, transform: 'scale(0.8)' }),
        animate('0.15s ease-in-out')
      ]),
      transition('* => void', [
        animate('0.15s ease-in-out', style({ opacity: 0, transform: 'scale(0.7)' }))
      ])
    ]),
    trigger('slideInRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('0.15s ease-in-out', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('0.15s ease-in-out', style({ transform: 'translateX(-100%)' }))
      ])
    ])
  ]
})
export class Step4GuardianComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() scrollUp = new EventEmitter<number>();
  @ViewChild('suggestBox', { static: false }) suggestBox?: ElementRef;

  checkerPath: string = `${environment.hRef}assets/fi_check.png`;

  title: string = 'Add Guardians';
  subtitle: string = 'Add one or more guardians below';

  guardianTypeLabel: string = 'Guardian Type';
  guardianTypeHint: string = 'Select guardian type';

  titleLabel: string = 'Title';
  titleHint: string = 'Title';
  fNameLabel: string = 'First Name';
  fNameHint : string = 'First name';
  mNameLabel : string = 'Middle Name';
  mNameHint : string = 'Middle name';
  surnameLabel : string = 'Surname';
  surnameHint : string = 'Last name';
  phoneLabel : string = 'Phone Number';
  emailLabel : string = 'Email Address';
  emailHint : string = 'Email address';

  idLabel: string = 'ID/Passport Number';
  idHint: string = 'Enter ID or Passport Number';
  kraPinLabel: string = 'KRA PIN';
  kraPinHint: string = 'Enter KRA PIN';
  idFileLabel: string = 'Upload copy of ID (front + back) / Passport';
  kraFileLabel: string = 'Upload KRA PIN';

  bensLabel: string = 'Select Beneficiaries';
  bensHint: string = 'Select all beneficiaries applicable';

  removeGuardianText: string = 'Remove Guardian';
  addButtonText: string = '+ Add Guardian';

  requiredFieldString: string = 'Required field';
  invalidDocString: string = 'Please attach a valid document';

  currentRecord: any;
  currentIndex: number = 0;
  BeneficiaryList: any[] = [];
  GuardianList: any[] = [];

  loading: boolean = false;
  loadingText: string = 'Saving Guardian';

  buttonText: string = 'Save details';
  buttonDisabled: boolean = false;

  loadingRemove: boolean = false;
  removeButtonText: string = this.removeGuardianText;

  pushDocs: boolean = false;

  addText: string = 'Guardian details saved';
  updateText: string = 'Guardian details updated';

  showSuggestHint: boolean = false;
  showAssignPopup: boolean = false;

  formErrors: { [key: string]: string } = {
    Title: '',
    GuardianBens: '',
    FirstName: '',
    MiddleName: '',
    LastName: '',
    Phone: '',
    Email: '',
    IDNo: '',
    KraPin: '',
    IDNoImage: '',
    KraPinImage: '',
    Category: '',
  };
  validationMessages: { [key: string]: {} } = {
    Title: { required: this.requiredFieldString },
    GuardianBens: { required: this.requiredFieldString },
    FirstName: { required: this.requiredFieldString },
    MiddleName: { required: this.requiredFieldString },
    LastName: { required: this.requiredFieldString },
    Phone: { required: this.requiredFieldString, invalid:'Invalid phone number', exist: 'This number is tied to an existing guardian' },
    Email: { required: this.requiredFieldString, email: "Invalid email address", exist: 'This email is tied to an existing guardian' },
    IDNo: { required: this.requiredFieldString, min:'Invalid ID or Passport number', exist: 'This ID is tied to an existing guardian' },
    KraPin: { required: this.requiredFieldString, pattern:'Invalid KRA PIN', exist: 'This PIN is tied to an existing guardian' },
    IdNoImage: { required: this.requiredFieldString },
    KraPinImage: { required: this.requiredFieldString },
    Category: { required: this.requiredFieldString }
  };

  guardianForm = this._formBuilder.group({
    guardians: this._formBuilder.array([
      this.createGuardiansFormGroup()
    ]),
  });


  guardianPanels: ExpansionPanel[][] = [];
  private formChangesSubscription: Subscription[] = [];

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly notificationService: NotificationService,
    private readonly tabControlService: TabControlService,
    private readonly validationService: ValidationService,
    private readonly apiService: ApiService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    if (this.guardians && this.guardians.length > 0) {
      if (this.guardians[0]?.verified) {
        const guardiansArray = this.guardianForm.get('guardians') as FormArray;

        // Clear existing settlors
        while (guardiansArray.length !== 0) {
          guardiansArray.removeAt(0);
        }

        // Add beneficiaries from storage
        this.guardians.forEach((guardian: any) => {
          guardiansArray.push(this.createGuardiansFormGroup(false, guardian));
        });

        this.tabControlService.guardians = this.guardians;
      }
    }

    if (this.tabControlService.activeTab > 3) {
      this.populateData();
      
      this.BeneficiaryList = organizeValues(this.beneficiaries, 'Beneficiary');
      // this.GuardianList = organizeValues(this.guardians, 'Guardian');
      // this.showAssignPopup = true;

    }
    if (this.tabControlService.guardians && this.tabControlService.guardians.length > 0) {
      this.currentRecord = this.tabControlService.guardians[this.tabControlService.guardians.length-1]
    }
    this.updateValidity();

    this.setupFormChangeListeners();
    this.guardianForm.valueChanges.subscribe((form) => {
      this.logErrors();
      this.updateValidity();
    });
  }
  ngAfterViewInit(): void {
    // Wait for templates to be available
    setTimeout(() => {
      if (this.guardians.length > 0) {
        this.guardians.forEach((_: any, i: number) => {
          this.guardianPanels[i] = this.generatePanels(i);
        });
      }
    });
  }
  ngOnDestroy(): void {
    this.cleanupFormChangeListeners();
  }

  logErrors() {
    this.formErrors = this.validationService.logValidationErrors(
      this.guardianForm,
      this.formErrors,
      this.validationMessages
    );
  }

  suggestions: any = null;
  autofilled: boolean = false;

  get region(): any {
    return this.tabControlService.regionType;
  }

  get settlors(): any {
    return this.tabControlService.settlors;
  }

  get beneficiaries(): any {
    return this.tabControlService.beneficiaries;
  }

  get GuardianOptions() {
    return this.tabControlService.GuardianOptions;
  };

  get guardians(): any {
    return this.tabControlService.guardians;
  }

  get loadingTitles(): boolean {
    return this.tabControlService.loadingTitles;
  }
  get TitleOptions(): any {
    return this.tabControlService.TitleOptions;
  }

  get saveProgress(): ()=> void {
    return this.tabControlService.saveProgress;
  }

  updateValidity() {
    if (this.guardianForm.valid) {
      if (this.loading){ this.buttonDisabled = true;}
      else {this.buttonDisabled = false;}
    } else {
      this.buttonDisabled = true;
    }
    
  };

  async populateData(): Promise<void> {
    if (this.TitleOptions.length === 0) {
      this.tabControlService.loadingTitles = true;
      await this.tabControlService.getTitlesList();
      this.tabControlService.loadingTitles = false;
    }
  }

  updateIDObject(index: number, object: any): void {
    if (!object) return;
    const currents = this.guardians;
    const latestRecord = currents[index];
    let updatedRecord = {
      ...latestRecord,
      IDNoImage: object
    };

    Object.assign(currents[index], updatedRecord);
    this.tabControlService.updateRecord(4, currents);
  }
  updateKraObject(index: number, object: any): void {
    if (!object) return;
    const currents = this.guardians;
    const latestRecord = currents[index];
    let updatedRecord = {
      ...latestRecord,
      KraPinImage: object
    };

    Object.assign(currents[index], updatedRecord);
    this.tabControlService.updateRecord(4, currents);
  }

  briefTimeout: any;
  showSuggestBrief(): void {
    setTimeout(() => {
      this.showSuggestHint = true;
    }, 1000);

    this.briefTimeout = setTimeout(() => {
      this.showSuggestHint = false;
    }, 30000);
  }

  async suggestSettlors(type: 'ID' | 'NAME' , val: string): Promise<any> {
    if (this.autofilled) {
      this.resetForm();
    }
    
    this.suggestions = null;
    let validEntries: any = [];

    if (val.length > 2) {
      try {
        const fields = this.guardianForm.get('guardians') as FormArray;

        for (const element of this.settlors) {
          let exist = false;
          let trueStuff = element.FirstName.includes(val);

          if (type === 'ID') {
            trueStuff = element.IDNo.includes(val);
          }

          if (trueStuff) {
            for (let j = 0; j < fields.length - 1; j++) {
              if (
                  fields.at(j).get('IDNo')?.value === element.IDNo ||
                  fields.at(j).get('Phone')?.value === element.Phone ||
                  fields.at(j).get('KraPin')?.value === element.KraPin) {
                  exist = true;
              } else {
                this.showSuggestHint = false;
                clearTimeout(this.briefTimeout);
              }
            }

            if (!exist && validEntries.length < 3 && !validEntries.some((entry: { IDNo: string; }) => entry.IDNo === element.IDNo)) {
              validEntries.push(element);
            }
          }
        }

        if (validEntries.length > 0) {
          this.suggestions = validEntries;
        }

        if (this.suggestions !== null) {
          const childDivHeight = this.suggestBox?.nativeElement.clientHeight;
          this.scrollUp.emit(childDivHeight);
        }
      } catch (error) {
          console.error('suggestSettlors! ', error);
      }
    }

    return this.suggestions;
  }

  resetForm(): void {
    try {
      this.autofilled = false;
      this.pushDocs = false;

      let currentGuardians = this.tabControlService.guardians;
      const latestRecord = currentGuardians[currentGuardians.length - 1];
      latestRecord.verified = false;

      Object.assign(currentGuardians[currentGuardians.length - 1], latestRecord);
      this.tabControlService.updateRecord(4, currentGuardians);

      const guradian = this.getGuardians().at(this.getGuardians().length-1) as FormGroup;
      guradian.get('Title')?.setValue('');
      guradian.get('FirstName')?.setValue('');
      guradian.get('MiddleName')?.setValue('');
      guradian.get('LastName')?.setValue('');
      guradian.get('Phone')?.setValue('7');
      guradian.get('Email')?.setValue('');
      guradian.get('IDNo')?.setValue('');
      guradian.get('KraPin')?.setValue('');
      guradian.get('IDNoImage')?.setValue(null);
      guradian.get('KraPinImage')?.setValue(null);

      this.validationService.addOrRemoveValidator(false, guradian.get('IDNoImage')!);
      this.validationService.addOrRemoveValidator(false, guradian.get('KraPinImage')!);

    } catch (error) {
      console.log('Error!! ', error);
    }
  }


  patchSuggestedValues(index: number, value: any): void {
    try {
      if (value) {
        const fields = this.guardianForm.get('guardians') as FormArray;
        fields.removeAt(index);
        fields.push(this.createGuardiansFormGroup(true, value));

        const guradian = fields.at(index) as FormGroup;

        this.validationService.addOrRemoveValidator(true, guradian.get('IDNoImage')!);

        if (this.region == 0)
          this.validationService.addOrRemoveValidator(true, guradian.get('KraPinImage')!);

        // console.log('Guradian \n', guradian);
      //  fields.setControl(index, guradian);
        this.guardianForm.setControl('guardians', fields);
        
        this.suggestions = null;
        this.autofilled = true;
        this.pushDocs = true;
      }
      this.updateValidity();

    } catch (error) {
      console.log('ERR ??? ', error);
    }
  }

  createGuardiansFormGroup(patch: boolean=false, patchData: any=null, index?: number): FormGroup {
    let currentGuardians = this.tabControlService.guardians;
    let form: FormGroup;
    
    let category = this.tabControlService.GuardianOptions[0];

    if (this.guardianForm && this.getGuardians().length > 0) {
      category = this.tabControlService.GuardianOptions[1];
    }

    if (patchData) {
      const currIndex = index ?? currentGuardians.length-1;

      if (patch) {
        this.autofilled = true;
        this.pushDocs = true;

        form = this._formBuilder.group({
          Title: [patchData.Title || ''],
          Category: [category , Validators.required],
          GuardianBens: [, Validators.required],
          FirstName: [patchData.FirstName , Validators.required],
          MiddleName: [patchData.MiddleName || ''],
          LastName: [patchData.LastName, Validators.required],
          Phone: [patchData.Phone, Validators.required],
          Email: [patchData.Email, [Validators.required, Validators.email]],
          IDNo: [patchData.IDNo, [Validators.required, Validators.min(4)]],
          KraPin: [patchData.KraPin || '', this.region == 1 ? [Validators.pattern(/^[AP]\d{9}[A-Z]$/) ] : [Validators.required, Validators.pattern(/^[AP]\d{9}[A-Z]$/) ]],
          IDNoImage: [],
          KraPinImage: [],
        });



        this.updateIDObject(currIndex, patchData.IDNoImage);
        this.updateKraObject(currIndex, patchData.KraPinImage);

        const latestRecord = currentGuardians[currIndex];
        latestRecord.verified = true; // Helpful in repeating values validation
        Object.assign(currentGuardians[currIndex], latestRecord);
        this.tabControlService.updateRecord(4, currentGuardians);

      } else {
        form = this._formBuilder.group({
          Title: [patchData.Title || ''],
          Category: [patchData.Category || category , Validators.required],
          GuardianBens: [, Validators.required],
          FirstName: [patchData.FirstName , Validators.required],
          MiddleName: [patchData.MiddleName || ''],
          LastName: [patchData.LastName || '', Validators.required],
          Phone: [patchData.Phone || '', Validators.required],
          Email: [patchData.Email || '', [Validators.required, Validators.email]],
          IDNo: [patchData.IDNo || '', [Validators.required, Validators.min(4)]],
          KraPin: [patchData.KraPin || '', this.region == 1 ? [Validators.pattern(/^[AP]\d{9}[A-Z]$/) ] : [Validators.required, Validators.pattern(/^[AP]\d{9}[A-Z]$/) ]],
          IDNoImage: [],
          KraPinImage: [],
        });

        if (patchData.GuardianBens && patchData.GuardianBens.length > 0) {
          let items = [];

          for (const element of patchData.GuardianBens) {
            if (element.BeneficiaryID) {
              items.push(Number(element.BeneficiaryID));
            } else {
              items.push(Number(element));
            }
          }

          form.get('GuardianBens')?.setValue(items);
        }

        if (!patchData.done && patchData.verified) {
          this.pushDocs = true;
          
          this.validationService.addOrRemoveValidator(true, form.get('IDNoImage')!);
          if (this.region == 0)
            this.validationService.addOrRemoveValidator(true, form.get('KraPinImage')!);
        }
      }

      if (patchData.IDNoImage) {
        form.get('IDNoImage')?.setValue(patchData.IDNoImage.stringValue);
      }
      if (patchData.KraPinImage) {
        form.get('KraPinImage')?.setValue(patchData.KraPinImage.stringValue);
      }

    } else {
      this.showSuggestBrief();

      this.autofilled = false;
      this.pushDocs = false;

      let currentRecord: any = {verified: false, done: false};


      if (
        this.guardians && this.guardians.length > 0 &&
        !(this.guardians[0].verified && this.guardians[0].verified == true)
      ) {
        currentRecord = {verified: false, done: false, Category: 'Principal Guardian'};
        this.tabControlService.refreshRecord(4);
        currentGuardians = this.tabControlService.guardians;
      }


      if ((this.guardianForm && this.getGuardians().length > 0) ||
        this.guardians &&
          (this.guardians.length > 0 &&
        !(this.guardians[0].verified && this.guardians[0].verified == true)) ||
        this.guardians.length == 0
      ) {
        currentGuardians.push(currentRecord);
        this.tabControlService.updateRecord(4, currentGuardians);
      }
      
      form = this._formBuilder.group({
        Title: [''],
        Category: [category , Validators.required],
        GuardianBens: [, Validators.required],
        FirstName: ['', Validators.required],
        MiddleName: [''],
        LastName: ['', Validators.required],
        Phone: ['', Validators.required],
        Email: ['', [Validators.required, Validators.email]],
        IDNo: ['', [Validators.required, Validators.min(4)]],
        KraPin: ['', this.region == 1 ? [Validators.pattern(/^[AP]\d{9}[A-Z]$/) ] : [Validators.required, Validators.pattern(/^[AP]\d{9}[A-Z]$/) ]],
        IDNoImage: [],
        KraPinImage: [],
      });
    }
      
    if (this.guardianForm && this.getGuardians().length > 0) {
      this.validationService.addOrRemove3Validators(
        true,
        form.get('Email')!,
        [Validators.required,
          Validators.email,
          createIDsValidatorEstate('EMAIL', this.getGuardians(), this.guardians)
        ]
      );
      this.validationService.addOrRemoveSpecificValidators(
        true,
        form.get('Phone')!,
        [Validators.required,
          createIDsValidatorEstate('TEL', this.getGuardians(), this.guardians)
        ]
      );
      this.validationService.addOrRemove3Validators(
        true,
        form.get('IDNo')!,
        [Validators.required,
          Validators.min(4),
          createIDsValidatorEstate('ID', this.getGuardians(), this.guardians)
        ]
      );

      if (this.region == 1) {
        this.validationService.addOrRemoveSpecificValidators(
          true,
          form.get('KraPin')!,
          [Validators.pattern(/^[AP]\d{9}[A-Z]$/),
            createIDsValidatorEstate('PIN', this.getGuardians(), this.guardians)
          ]
        );
      } else {
        this.validationService.addOrRemove3Validators(
          true,
          form.get('KraPin')!,
          [Validators.required,
            Validators.pattern(/^[AP]\d{9}[A-Z]$/),
            createIDsValidatorEstate('PIN', this.getGuardians(), this.guardians)
          ]
        );
      }
    }

    return form;
  };

  async addNewGuardian() {
    const fields = this.guardianForm.get('guardians') as FormArray;
    const newFormGroup = this.createGuardiansFormGroup();
    fields.push(newFormGroup);
    this.guardianForm.setControl('guardians', fields);
    this.buttonText = 'Save Details';

    // Wait for view to update
    await new Promise(resolve => setTimeout(resolve));
    const newIndex = fields.length - 1;
    this.guardianPanels[newIndex] = this.generatePanels(newIndex);

    // Set up listeners after panels are generated
    this.setupFormChangeListenersForIndex(newIndex);
    this.cdr.detectChanges();
  }

  async removeGuardian(i:number) {
    this.currentIndex = i;
    const fields = this.guardianForm.get('guardians') as FormArray;
    let currentGuardians = this.tabControlService.guardians;

    if (currentGuardians[i] && currentGuardians[i].GuardianID) {
      this.removeButtonText = 'Removing Guardian';
      this.loadingRemove = true;

      try {
        if (this.tabControlService.TrustID !== null) {
          const result = await this.tabControlService.removeRecord(
            'Guardians',
            currentGuardians[i].GuardianID,
            parseInt(this.tabControlService.TrustID),
            'Guardian'
          );
  
          if (result === 1) {
            this.notificationService.viewToast('success', 'Guardian removed successfully');
            fields.removeAt(i);
            // Remove guardian at index
            currentGuardians.splice(i, 1);;
            this.tabControlService.updateRecord(4, currentGuardians);
          } else {
            this.notificationService.viewToast('error', 'Error removing record');
          }
        } else {
          console.log('Relevant IDs not found');
        }
      } catch (error) {
        console.error(':: Error !! ', error);
      }
      this.removeButtonText = this.removeGuardianText;
      this.buttonText = 'Save details';
      this.loadingRemove = false;
    } else {
      if (currentGuardians[i]) {
        fields.removeAt(i);
        // Remove settlor at index
        currentGuardians.splice(i, 1);;
        this.tabControlService.updateRecord(4, currentGuardians);
      } else {
        fields.removeAt(i);
      }
    }

    setTimeout(()=> {
      this.generatePanels(i);
      this.guardians.forEach((guardian: any, index: number) => {
        this.updateDoneStatus(index, guardian);
      });
    })
  }
  
  getGuardians() : FormArray {  
    return this.guardianForm.get("guardians") as FormArray  
  }
  getGuardiansFormGroup(index: number): FormGroup {
    const guardians = this.guardianForm.get('guardians') as FormArray;
    return guardians.at(index) as FormGroup;
  }

  async saveGuardian(index: number, record: FormGroup): Promise<void> {
    if (this.guardians[index].verified && !this.autofilled) this.loadingText = 'Updating Guardian';
    else this.loadingText = 'Saving Guardian';
    let tsMessage = this.addText;

    this.notificationService.isLoading(true);
    try {
      let data;
      
      if (index === 0) {
        data = {...record.getRawValue(), ...{TrustID: this.tabControlService.TrustID, Category: 'Principal Guardian'}};
      } else {
        data = {...record.getRawValue(), ...{TrustID: this.tabControlService.TrustID}};
      }

      let currentGuardians = this.guardians;

      if (this.guardians[index].verified || this.autofilled) {
        data.IDNoImage = this.guardians[index].IDNoImage;
        data.KraPinImage = this.guardians[index].KraPinImage;

        let guardsBens = [];
        for (let i=0; i<data.GuardianBens.length; i++) {
          let id = '';

          if (currentGuardians[index].GuardianBens
              && currentGuardians[index].GuardianBens[i]
              && currentGuardians[index].GuardianBens[i].ID
              && currentGuardians[index].GuardianBens[i].ID !== ''
              && currentGuardians[index].GuardianBens[i].BeneficiaryID
              && currentGuardians[index].GuardianBens[i].BeneficiaryID === data.GuardianBens[i].toString()
            ) {

              id = currentGuardians[index].GuardianBens[i].ID;
          }

          const item = {
            ID: id.toString(),
            BeneficiaryID: data.GuardianBens[i].toString()
          };

          guardsBens.push(item);
        }
        // Organize for middleware
        data.GuardianBens = guardsBens;
        // console.log('Data ?? ', data);

      } else {
        data.IDNoImage = {name: '', stringValue: ''};
        data.KraPinImage = {name: '', stringValue: ''};

        data.GuardianBens = [];
      }

      if (currentGuardians[index].GuardianID) {
        data = {...data, ...{GuardianID: currentGuardians[index].GuardianID}};
      }

      const response = await this.apiService.postRequest(
        environment.baseUrl + Constants.saveGuardianURL, data);

      // console.log('::::: RESPONSE ::::::\n', response);

      if (response.Status === 1) {
        let currentGuardians = this.tabControlService.guardians;

        const latestRecord = currentGuardians[index];
        const recordValueWithoutImages = Object.fromEntries(
          Object.entries(record.getRawValue()).filter(([key]) => !key.includes('Image'))
        );

        const updatedRecord = {
          ...latestRecord,
          ...recordValueWithoutImages,
          ...{GuardianID: response.GuardianID}};
        Object.assign(currentGuardians[index], updatedRecord);

        this.tabControlService.updateRecord(4, currentGuardians);

        if (this.guardians[index].verified || this.autofilled) {
          tsMessage = this.updateText;
          let currentGuardians = this.tabControlService.guardians;
          let currentRecord = currentGuardians[index];
          
          if (this.autofilled) {
            tsMessage = this.addText;
            currentRecord.verified = true;
          }
          currentRecord.done = true;
          
          if (data.IDNoImage && data.IDNoImage.stringValue.length > 5 && response.IdURL) {
            this.tabControlService.removeFromDocSummary('ID', 'Guardian', currentRecord.GuardianID);
            this.tabControlService.updateDocSummary(response.IdURL);
            currentRecord = {...currentRecord, ...{IDURL: response.IdURL, SavedID: currentRecord.IDNoImage}};
          }
          if (data.KraPinImage && data.KraPinImage.stringValue.length > 5 && response.KraURL) {
            this.tabControlService.removeFromDocSummary('PIN', 'Guardian', currentRecord.GuardianID);
            this.tabControlService.updateDocSummary(response.KraURL);
            currentRecord = {...currentRecord, ...{KraURL: response.KraURL, SavedPIN: currentRecord.KraPinImage}};
          }

          if (response.Beneficiaries && response.Beneficiaries.length > 0) {
            currentRecord.GuardianBens = response.Beneficiaries;
          }
          
          Object.assign(currentGuardians[index], currentRecord);
          this.tabControlService.updateRecord(4, currentGuardians);
          
          this.autofilled = false;

        } else {
          // Update guardian progress
          let currentGuardians = this.tabControlService.guardians;
          let currentRecord = currentGuardians[index];
          currentRecord.verified = true;
          // currentRecord = {...currentRecord, ...{verified: true, done: false}};
          Object.assign(currentGuardians[index], currentRecord);
          this.tabControlService.updateRecord(4, currentGuardians);

          this.buttonText = 'Upload documents';

          this.validationService.addOrRemoveValidator(true, this.getGuardiansFormGroup(index).get('IDNoImage')!);
          if (this.region == 0)
            this.validationService.addOrRemoveValidator(true, this.getGuardiansFormGroup(index).get('KraPinImage')!);
          this.updateValidity();
          
          if (this.guardians.length <= 1) this.tabControlService.updateTrustProgress('Guardians');
        }

        if (!environment.production) console.log('>> New Guardians !! \n', this.tabControlService.guardians);
        // console.log('>> Current Formgroup !! \n', this.getGuardians());
        this.notificationService.viewToast('success', tsMessage);
        this.updateDoneStatus(index, this.guardians[index]);

      } else {
        this.notificationService.viewToast('error', response.Message);
      }
    } catch (error) {
      console.error(':: Error !! ', error);
      this.notificationService.viewToast('error', 'An unexpected error occured');
    }
    this.notificationService.isLoading(false);
    this.loading = false;
  }

  async updateProgress(): Promise<void> {
    if (this.guardians[this.guardians.length-1].done == false ) {
      await this.saveGuardian(this.getGuardians().length-1, this.getGuardiansFormGroup(this.getGuardians().length-1));
    }
    this.saveProgress();
  }

  async updateRecord(index: number) {
    await this.saveGuardian(index, this.getGuardiansFormGroup(index));
  }

  async submitForm(): Promise<void> {
    // console.log(':: RECORD DONE? !! ', this.guardians[this.guardians.length-1].done);
    if (this.guardians[this.guardians.length-1].done) {
      this.navigateFunction();
    } else {
      this.saveGuardian(this.getGuardians().length-1, this.getGuardiansFormGroup(this.getGuardians().length-1));
    }
  }

  toggleAssignPopup(value: boolean): void {
    this.showAssignPopup = value;
  }

  async openAssignPopup(): Promise<void> {
    this.toggleAssignPopup(true);
  }

  closeAssignPopup = async() => {
    this.toggleAssignPopup(false);
    await this.tabControlService.changeTab(5);
    this.tabControlService.page4Done = true;
  }

  async navigateFunction(): Promise<void> {
    const unassignedBeneficiaries = this.checkAllBeneficiariesAssigned(this.beneficiaries, this.guardians);

    if (unassignedBeneficiaries.length === 0) {
      await this.tabControlService.changeTab(5);
      this.tabControlService.page4Done = true;
    } else {
      const filteredArray = this.filterBeneficiariesArray(this.beneficiaries, unassignedBeneficiaries);
      const leftBens = organizeNames(filteredArray);
      // this.openAssignPopup();

      this.notificationService.viewToast(
        'info',
        'Please assign all beneficiaries to a guardian before proceeding',
        `Expand the 'Personal Information' panel to assign beneficiaries : (${leftBens.join(', ')})`
      );
    }
  }

  checkAllBeneficiariesAssigned(mainArray: any[], secondaryParentArray: any[]): string[] {
    const beneficiaryIDs = mainArray.map(obj => obj.BeneficiaryID.toString()); // Convert BeneficiaryID to string for comparison
    const allSecondaryBeneficiaryIDs: string[] = [];

    // Iterate over each object in the secondaryParentArray
    secondaryParentArray.forEach(parentObj => {
        if (parentObj.GuardianBens && parentObj.GuardianBens.length > 0) {
          const secondaryBeneficiaryIDs = parentObj.GuardianBens.map((obj: { BeneficiaryID: { toString: () => any; }; }) => obj.BeneficiaryID.toString()); // Convert BeneficiaryID to string for comparison
          allSecondaryBeneficiaryIDs.push(...secondaryBeneficiaryIDs);
        }
    });

    // Find missing BeneficiaryIDs from the mainArray in the allSecondaryBeneficiaryIDs
    const missingBeneficiaryIDs = beneficiaryIDs.filter(id => !allSecondaryBeneficiaryIDs.includes(id));
    return missingBeneficiaryIDs;
  }

  filterBeneficiariesArray(mainArray: any[], missingBeneficiaryIDs: string[]): any[] {
    const result = mainArray.filter(obj => missingBeneficiaryIDs.includes(obj.BeneficiaryID.toString()));
    return result;
  }

  // ==============  EXPANSION SECTION   =================
  @ViewChild('guardianInfo') guardianInfo!: TemplateRef<any>;
  @ViewChild('uploadDocs') uploadDocs!: TemplateRef<any>;


    // Panels
  generatePanels = (i: number): ExpansionPanel[] => {
    // Fallback if templates not ready
    if (!this.guardianInfo || !this.uploadDocs || !this.guardians[i]) {
      return [];
    }
    return [
      {
        title: 'Personal Information',
        contentTemplate: this.guardianInfo,
        context: { index: i },
        expanded: !(this.guardians[i].verified),
        done: (this.guardians[i].verified)
      },
      {
        title: 'Upload Documents',
        contentTemplate: this.uploadDocs,
        context: { index: i },
        disabled: !(this.guardians[i].verified),
        expanded: (this.guardians[i].verified && !this.guardians[i].done),
        done: (this.guardians[i].done)
      }
    ];
  }
  // Done Status updates
  updateDoneStatus(i: number, value: any) {
    // Ensure panels array exists
    this.guardianPanels = this.guardianPanels || [];
    
    // Initialize if missing or invalid
    if (this.guardianPanels[i]?.length !== 2) {
      this.guardianPanels[i] = this.generatePanels(i);
    }

    const panels = this.guardianPanels[i];
    
    // Update panel states while preserving context
    if (panels[0]) {
      panels[0].done = value.verified;
      // Preserve existing context or initialize if missing
      panels[0].context = panels[0].context || { index: i };
      panels[0].expanded = !value.verified;
    }
    
    if (panels[1]) {
      panels[1].done = value.done;
      panels[1].context = panels[1].context || { index: i };
      panels[1].disabled = !value.verified;
      panels[1].expanded = !value.done;
    }

    setTimeout(()=> {
      try {
        if (!value.done && i == this.settlors.length-1) {
          this.notificationService.viewToast(
            'info',
            `Guardian ${value.FirstName} ${value.LastName}`,
            `Please continue to ${panels[1].title}`
          );
        }
        
      } catch (error) {}
    })
  }
  //  Form Change Listeners
  private setupFormChangeListeners() {
    this.cleanupFormChangeListeners();
    
    this.getGuardians().controls.forEach((control, index) => {
      // General form changes
      const subscription = (control as FormGroup).valueChanges.subscribe(() => {
        this.cdr.detectChanges();
      });
      this.formChangesSubscription.push(subscription);
    });
  }
  private setupFormChangeListenersForIndex(index: number) {
    // Clean up existing subscription if any
    const existingSub = this.formChangesSubscription[index];
    if (existingSub) {
      existingSub.unsubscribe();
    }
  }
  private cleanupFormChangeListeners() {
    this.formChangesSubscription.forEach(sub => sub.unsubscribe());
    this.formChangesSubscription = [];
  }

  private readonly loggedErrors = new Set<string>();

  confirmFormMatchesRecord(index: number): boolean {
    try {
      const guardian = this.guardians[index];
      const formGroup = this.getGuardiansFormGroup(index);
      
      if (!guardian?.done || !formGroup) {
        return true;
      }

      const fieldsToCompare = [
        'Title', 'FirstName', 'MiddleName', 'LastName', 'Category',
        'Phone', 'Email', 'IDNo', 'KraPin'
      ];

      // Compare scalar fields
      const basicFieldsMatch = fieldsToCompare.every(field => {
        const guardianValue = guardian[field];
        const formValue = formGroup.get(field)?.value;
        const valuesMatch = (formValue || '') === (guardianValue || '');

        return valuesMatch;
      });

      // Compare multi-select GuardianBens array
      const formBens: any[] = formGroup.get('GuardianBens')?.value || [];
      const recordBens: any[] = guardian.GuardianBens || [];

      // Normalize form beneficiary IDs to sorted strings
      const formBenIds = formBens
        .map(item => (typeof item === 'object' ? item?.BeneficiaryID : item)?.toString())
        .filter(Boolean)
        .sort();

      // Normalize record beneficiary IDs to sorted strings
      const recordBenIds = recordBens
        .map(item => (typeof item === 'object' ? item?.BeneficiaryID : item)?.toString())
        .filter(Boolean)
        .sort();

      const bensMatch =
        formBenIds.length === recordBenIds.length &&
        formBenIds.every((id, idx) => id === recordBenIds[idx]);

      let filesMatch = true;

      if (guardian?.KraPinImage?.name) {
        filesMatch =
          guardian.IDNoImage.name === guardian.SavedID.name  &&
          guardian.KraPinImage.name === guardian.SavedPIN.name;
      } else {
        filesMatch = guardian.IDNoImage.name === guardian.SavedID.name;
      }

      return basicFieldsMatch && bensMatch && filesMatch;
    } catch (error: any) {
      const errorKey = `error-${index}-${error.message}`;
      if (!this.loggedErrors.has(errorKey)) {
        console.error('Error in confirmFormMatchesRecord:', error);
        this.loggedErrors.add(errorKey);
      }
      return false;
    }
  }

  // +++++++++++++++++++++++++++++++++++++++++++++++++++++
}
